import { useState } from "react";
import { Button, PageContainer } from "@repo/ui";
import { useAuthToken } from "@convex-dev/auth/react";
import { useAction, useMutation, useQuery } from "convex/react";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  EnvoyerLienParcoursDialog,
  FicheCandidat,
  ResultatsEpreuve,
  cleEpreuve,
  useEnvoiLienParcours,
  useVueFiche,
} from "@repo/campagne";
import { SendResultsDialog } from "./ParcoursDetailDialogs";
import { Link, useParams, useSearchParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ParcoursInfoCard } from "./ParcoursInfoCard";
import { TypeEvalEnum } from "@convex/schema";
import { getEvaluationTitle } from "@repo/utils";

/** URL du déploiement, pour les routes `httpAction` (rapports PDF). */
const CONVEX_URL = process.env.BUN_PUBLIC_CONVEX_URL || "http://127.0.0.1:3210";

type EmailStatus = "idle" | "sending" | "success" | "error";

/** Résultats bruts d'une épreuve, lus avec la query org-scopée du portail. */
function ResultatsEpreuveClient({
  evaluationId,
  token,
}: {
  evaluationId: Id<"evaluations">;
  token: string | null;
}) {
  const resultat = useQuery(api.evaluations.obtenirEvaluationPourAdmin, {
    evaluationId,
  });
  // Rapports proposés (candidat, recruteur…) : liste vide pour un type qui
  // n'en a pas, et le menu ne s'affiche alors pas.
  const rapportsDisponibles = useQuery(api.reports.rapportsDisponibles, {
    evaluationId,
  });

  if (resultat === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (resultat === null) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        Évaluation introuvable.
      </p>
    );
  }
  return (
    <ResultatsEpreuve
      resultat={resultat}
      rapportsDisponibles={rapportsDisponibles}
      token={token}
      convexUrl={CONVEX_URL}
    />
  );
}

/**
 * Fiche d'un candidat avec les queries et actions du portail client. Rendue en
 * pleine page (`/p/:id`) et dans le panneau du détail de campagne.
 */
export function FicheCandidatClient({
  parcoursId,
}: {
  parcoursId: Id<"parcoursEvaluations">;
}) {
  const token = useAuthToken();

  // State pour la modale d'envoi des résultats
  const [resultatsModalOpen, setResultatsModalOpen] = useState(false);
  const [resultatsStatus, setResultatsStatus] = useState<EmailStatus>("idle");
  const [resultatsMessage, setResultatsMessage] = useState("");

  const archiverParcours = useMutation(
    api.parcoursEvaluations.archiverParcours,
  );
  const desarchiverParcours = useMutation(
    api.parcoursEvaluations.desarchiverParcours,
  );
  const envoyerResultatsParcours = useAction(
    api.email.envoyerResultatsParcours,
  );

  const result = useQuery(api.parcoursEvaluations.obtenirDetailParcoursAdmin, {
    parcoursId,
  });

  const envoiLien = useEnvoiLienParcours({
    parcoursId,
    emailCandidat: result?.parcours.emailCandidat,
  });

  // Passations de la campagne, pour situer le candidat parmi les autres
  // (matching, rang). Chargées seulement pour les épreuves dont la fiche
  // affiche le matching.
  const vue = useVueFiche();
  const campagneId = result?.parcours.campagneId;
  const aEpreuve = (cle: string) =>
    result?.evaluations.some((e) => cleEpreuve(e) === cle) ?? false;
  const charger = (cle: string) =>
    campagneId !== undefined && aEpreuve(cle) && vue.afficheMatching(cle);

  const campagne = useQuery(
    api.campagnes.obtenirCampagne,
    campagneId && (charger("pami") || charger("pc-fit") || charger("fairview"))
      ? { campagneId }
      : "skip",
  );
  const evaluationsPami = useQuery(
    api.evaluations.obtenirEvaluationsPamiParCampagne,
    campagneId && charger("pami") ? { campagneId } : "skip",
  );
  const evaluationsFitCulturel = useQuery(
    api.evaluations.obtenirEvaluationsFitCulturelParCampagne,
    campagneId && charger("pc-fit") ? { campagneId } : "skip",
  );
  const evaluationsEntStruct = useQuery(
    api.evaluations.obtenirEvaluationsEntStructParCampagne,
    campagneId && charger("fairview") ? { campagneId } : "skip",
  );

  const handleSendResultats = async () => {
    setResultatsStatus("sending");
    setResultatsMessage("Envoi en cours...");

    try {
      const res = await envoyerResultatsParcours({ parcoursId });

      if (res.succes) {
        setResultatsStatus("success");
        setResultatsMessage(res.message);
      } else {
        setResultatsStatus("error");
        setResultatsMessage(res.message || "Une erreur est survenue.");
      }
    } catch (error) {
      setResultatsStatus("error");
      setResultatsMessage(
        error instanceof Error
          ? error.message
          : "Une erreur inattendue est survenue.",
      );
    }
  };

  const closeResultatsModal = () => {
    setResultatsModalOpen(false);
    setResultatsStatus("idle");
    setResultatsMessage("");
  };

  if (result === undefined) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (result === null) {
    return (
      <div className="py-8 text-center">
        <h1 className="text-2xl font-bold text-destructive">
          Parcours introuvable
        </h1>
        <p className="mt-2 text-muted-foreground">
          Le parcours demandé n'existe pas.
        </p>
      </div>
    );
  }

  const { parcours, evaluations, progression } = result;

  const hasAutodiag = evaluations.some(
    (e) => e.typeEval === TypeEvalEnum.FORM && e.template === "autodiag",
  );
  const hasInbasket = evaluations.some(
    (e) => e.typeEval === TypeEvalEnum.INBASKET,
  );
  const peutEnvoyerResultats =
    hasAutodiag && hasInbasket && progression.pourcentage === 100;

  return (
    <>
      <FicheCandidat
        parcoursId={parcoursId}
        entete={
          <ParcoursInfoCard
            parcoursId={parcoursId}
            prenomCandidat={parcours.prenomCandidat}
            nomCandidat={parcours.nomCandidat}
            emailCandidat={parcours.emailCandidat}
            titre={parcours.titre}
            dateCreation={parcours.dateCreation}
            dateModification={parcours.dateModification}
            lienEnvoye={parcours.lienEnvoye}
            archive={parcours.archive}
            anonyme={parcours.anonyme}
            evaluations={evaluations}
            peutEnvoyerResultats={peutEnvoyerResultats}
            onOpenEmailModal={envoiLien.ouvrir}
            onOpenResultatsModal={() => {
              setResultatsStatus("idle");
              setResultatsMessage("");
              setResultatsModalOpen(true);
            }}
            onArchive={() => void archiverParcours({ parcoursId })}
            onDesarchive={() => void desarchiverParcours({ parcoursId })}
          />
        }
        epreuves={evaluations}
        progression={progression}
        campagne={
          campagneId
            ? {
                campagneId,
                campagneNom: campagne?.nom,
                evaluationsPami,
                evaluationsFitCulturel,
                evaluationsEntStruct,
              }
            : undefined
        }
        renderResultats={(epreuve) => (
          <ResultatsEpreuveClient
            key={epreuve.evalId}
            evaluationId={epreuve.evalId}
            token={token}
          />
        )}
        token={token}
        convexUrl={CONVEX_URL}
      />

      <SendResultsDialog
        isOpen={resultatsModalOpen}
        onClose={closeResultatsModal}
        status={resultatsStatus}
        message={resultatsMessage}
        evaluationTitles={evaluations.map((e) => getEvaluationTitle(e))}
        emailCandidat={parcours.emailCandidat}
        onSend={handleSendResultats}
      />

      <EnvoyerLienParcoursDialog
        envoi={envoiLien}
        emailCandidat={parcours.emailCandidat}
        lienEnvoye={parcours.lienEnvoye}
      />
    </>
  );
}

/** Fiche candidat en pleine page : lien partageable, et parcours hors campagne. */
export function ParcoursDetail() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const backPath = searchParams.get("from") ?? "/campagnes";

  return (
    <PageContainer style={{ scrollbarGutter: "stable" }}>
      <div className="mb-6">
        {/* Un vrai lien : le retour est une navigation, il doit s'ouvrir dans
            un onglet et se copier comme tel. */}
        <Button variant="ghost" size="sm" className="-ml-3" asChild>
          <Link to={backPath}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Retour
          </Link>
        </Button>
      </div>
      {id && <FicheCandidatClient parcoursId={id as Id<"parcoursEvaluations">} />}
    </PageContainer>
  );
}
